import CancelIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import { useFormState } from 'react-hook-form-mui';
import { FormattedMessage } from 'react-intl';
import { pushTo } from '../../core/routing';
import history from '../../core/routing/history';

export type FormActionsMuiRenderContainer = (
  leftActions: React.ReactNode,
  rightActions: React.ReactNode,
) => React.ReactNode;

export function FormActionsMui({
  saveLabel,
  disableOnSubmit = false,
  disableSave = false,
  successState = false,
  saveIcon = <SaveIcon />,
  cancel,
  separator = false,
  marginTop = 4,
  variant = 'default',
  onCancel,
  onDiscard,
  onDraft,
  renderContainer,
  disableSubmitIfInvalid = false,
  submitDisabledTooltip,
}: {
  saveLabel?: string;
  disableOnSubmit?: boolean;
  disableSave?: boolean;
  /** When true, save button is only enabled when form is dirty. When not dirty, disables save; tick is shown only after form has been submitted. */
  successState?: boolean;
  cancel?: boolean | string;
  onCancel?: () => void;
  /** When both provided, show Discard and Draft instead of a single Cancel button. */
  onDiscard?: () => void;
  onDraft?: () => void;
  saveIcon?: React.ReactNode;
  separator?: boolean;
  marginTop?: number;
  /** When "compact", draft and discard are rendered as icon buttons with tooltips. */
  variant?: 'default' | 'compact';
  /** Optional custom layout. Receives left actions (discard/cancel) and right actions (draft/submit). Default renders them in a single row. */
  renderContainer?: FormActionsMuiRenderContainer;

  disableSubmitIfInvalid?: boolean;
  /** Shown when the submit button is disabled. Wraps the button so the tooltip is visible on hover (e.g. to explain why submit is disabled). */
  submitDisabledTooltip?: React.ReactNode;
}) {
  const state = useFormState();
  const useDiscardDraft = onDiscard != null && onDraft != null;
  const allowCancel = useDiscardDraft ? false : (cancel ?? onCancel);

  const isSavedAndClean = successState && !state.isDirty;
  const showTick = isSavedAndClean && state.isSubmitted;
  const saveDisabled =
    disableSave || isSavedAndClean || (disableSubmitIfInvalid && state.isValid === false);
  const secondaryDisabled = state.isSubmitting || (disableOnSubmit && state.isSubmitted);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }

    if (cancel === true) {
      history.goBack();
    } else if (typeof cancel === 'string') {
      pushTo(cancel);
    }
  };

  const isCompact = variant === 'compact';

  const leftActions = (
    <>
      {useDiscardDraft &&
        (isCompact ? (
          <Tooltip
            title={<FormattedMessage id="common.actions.discard" defaultMessage="Descartar" />}
          >
            <span>
              <IconButton
                color="secondary"
                onClick={onDiscard}
                disabled={secondaryDisabled}
                size="small"
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            color="secondary"
            startIcon={<DeleteOutlinedIcon />}
            onClick={onDiscard}
            disabled={secondaryDisabled}
          >
            <FormattedMessage id="common.actions.discard" defaultMessage="Descartar" />
          </Button>
        ))}
      {allowCancel && (
        <Button
          color="secondary"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          disabled={secondaryDisabled}
        >
          <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
        </Button>
      )}
    </>
  );

  const rightActions = (
    <>
      {useDiscardDraft &&
        (isCompact ? (
          <Tooltip title={<FormattedMessage id="common.actions.draft" defaultMessage="Borrador" />}>
            <span>
              <IconButton
                color="secondary"
                onClick={onDraft}
                disabled={secondaryDisabled}
                size="small"
              >
                <EditNoteIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            color="secondary"
            startIcon={<EditNoteIcon />}
            onClick={onDraft}
            disabled={secondaryDisabled}
          >
            <FormattedMessage id="common.actions.draft" defaultMessage="Borrador" />
          </Button>
        ))}
      {saveDisabled && submitDisabledTooltip ? (
        <Tooltip title={submitDisabledTooltip}>
          <span>
            <Button
              loading={state.isSubmitting || (disableOnSubmit && state.isSubmitted)}
              type="submit"
              variant="contained"
              color="primary"
              disabled={saveDisabled}
              startIcon={showTick ? undefined : saveIcon}
              sx={{ minWidth: 130 }}
            >
              {showTick ? (
                <CheckIcon />
              ) : saveLabel ? (
                saveLabel
              ) : (
                <FormattedMessage id="common.form.saveBtnText" defaultMessage="Guardar" />
              )}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={state.isSubmitting || (disableOnSubmit && state.isSubmitted)}
          type={'submit'}
          variant="contained"
          color="primary"
          disabled={saveDisabled}
          startIcon={showTick ? undefined : saveIcon}
          sx={{ minWidth: 130 }}
        >
          {showTick ? (
            <CheckIcon />
          ) : saveLabel ? (
            saveLabel
          ) : (
            <FormattedMessage id="common.form.saveBtnText" defaultMessage="Guardar" />
          )}
        </Button>
      )}
    </>
  );

  const content = renderContainer ? (
    renderContainer(leftActions, rightActions)
  ) : (
    <Stack direction="row" spacing={2} alignItems="center">
      {leftActions}
      {rightActions}
    </Stack>
  );

  return (
    <Box mt={marginTop}>
      {separator && <Divider sx={{ my: 4 }} />}
      {content}
    </Box>
  );
}
